import {
    Button,
    IconButton,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
} from '@mui/material';
import { MessageDto, MessageStatus } from 'common/dto-types/message-dto';
import { useState, MouseEvent } from 'react';
import {
    CloseOutlined,
    SaveOutlined,
    SendOutlined,
    RocketOutlined,
    CopyOutlined,
    UndoOutlined,
    MoreOutlined,
    DeleteOutlined,
} from '@ant-design/icons';

export type MessageFormActionButtonProps = {
    message?: MessageDto;
    isSubmitting: boolean;
    isValid: boolean; // Formik's isValid - for strict validation (send test, publish)
    isBasicValid: boolean; // Custom basic validation - for save operations
    onCancel: () => void;
    onCreate?: () => void;
    onUpdate?: () => void;
    onUpdateWithoutExit?: () => void;
    onSendTest?: () => void;
    onPublish?: () => void;
    onDuplicate?: () => void;
    onDelete?: () => void;
    onRecall?: () => void;
    placement?: 'top' | 'bottom'; // For dropdown menu positioning
};

const MessageFormActionButton = ({
    message,
    isSubmitting,
    isValid,
    isBasicValid,
    onCancel,
    onCreate,
    onUpdate,
    onUpdateWithoutExit,
    onSendTest,
    onPublish,
    onDuplicate,
    onDelete,
    onRecall,
    placement = 'bottom',
}: MessageFormActionButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
    const [showSendTestConfirm, setShowSendTestConfirm] = useState(false);
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    const [showRecallConfirm, setShowRecallConfirm] = useState(false);

    const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleMenuAction = (action: () => void) => {
        handleCloseMenu();
        action();
    };

    const handleExitClick = () => {
        setShowExitConfirm(true);
    };

    const handleExitConfirm = () => {
        setShowExitConfirm(false);
        onCancel();
    };

    const handleExitCancel = () => {
        setShowExitConfirm(false);
    };

    const handleDeleteClick = () => {
        handleCloseMenu();
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        if (onDelete) {
            onDelete();
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    const handleDuplicateClick = () => {
        handleCloseMenu();
        setShowDuplicateConfirm(true);
    };

    const handleDuplicateConfirm = () => {
        setShowDuplicateConfirm(false);
        if (onDuplicate) {
            onDuplicate();
        }
    };

    const handleDuplicateCancel = () => {
        setShowDuplicateConfirm(false);
    };

    const handleSendTestClick = () => {
        handleCloseMenu();
        setShowSendTestConfirm(true);
    };

    const handleSendTestConfirm = () => {
        setShowSendTestConfirm(false);
        if (onSendTest) {
            onSendTest();
        }
    };

    const handleSendTestCancel = () => {
        setShowSendTestConfirm(false);
    };

    const handlePublishClick = () => {
        handleCloseMenu();
        setShowPublishConfirm(true);
    };

    const handlePublishConfirm = () => {
        setShowPublishConfirm(false);
        if (onPublish) {
            onPublish();
        }
    };

    const handlePublishCancel = () => {
        setShowPublishConfirm(false);
    };

    const handleRecallClick = () => {
        setShowRecallConfirm(true);
    };

    const handleRecallConfirm = () => {
        setShowRecallConfirm(false);
        if (onRecall) {
            onRecall();
        }
    };

    const handleRecallCancel = () => {
        setShowRecallConfirm(false);
    };

    // Scheduled or Published status: Show Duplicate, Recall, and Close icon
    if (message?.status === MessageStatus.Scheduled || message?.status === MessageStatus.Published) {
        return (
            <>
                <Stack direction="row" spacing={2} alignItems="center">
                    {onDuplicate && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CopyOutlined />}
                            disabled={isSubmitting}
                            onClick={handleDuplicateClick}
                        >
                            Duplicate
                        </Button>
                    )}
                    {onRecall && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<UndoOutlined />}
                            disabled={isSubmitting}
                            onClick={handleRecallClick}
                        >
                            Recall
                        </Button>
                    )}
                    <IconButton onClick={onCancel} disabled={isSubmitting} color="default">
                        <CloseOutlined />
                    </IconButton>
                </Stack>
                {/* Recall Confirmation Dialog */}
                <Dialog open={showRecallConfirm} onClose={handleRecallCancel}>
                    <DialogTitle>Recall Message</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {message.status === MessageStatus.Scheduled ? (
                                <>
                                    This message is scheduled for delivering, recall will move the message back to{' '}
                                    <strong>Draft</strong>.
                                </>
                            ) : (
                                <>
                                    This message is already published, recall will remove the notification history of
                                    recipients. <br />
                                    <strong>Note:</strong> Users may still interact with the notification banner from
                                    the system.
                                </>
                            )}
                            <br />
                            <br />
                            Do you want to continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleRecallCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleRecallConfirm} color="error" variant="contained" autoFocus>
                            Recall
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* Duplicate Confirmation Dialog */}
                <Dialog open={showDuplicateConfirm} onClose={handleDuplicateCancel}>
                    <DialogTitle>Duplicate Message</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            A new draft message will be created based on this message's content. Do you want to
                            continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDuplicateCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDuplicateConfirm} color="primary" variant="contained" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    // Recalled or Removed status: Show Duplicate and Close icon
    if (
        message?.status === MessageStatus.Recalled ||
        message?.status === MessageStatus.Removed ||
        message?.status === MessageStatus.Publishing
    ) {
        return (
            <>
                <Stack direction="row" spacing={2} alignItems="center">
                    {onDuplicate && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CopyOutlined />}
                            disabled={isSubmitting}
                            onClick={handleDuplicateClick}
                        >
                            Duplicate
                        </Button>
                    )}
                    <IconButton onClick={onCancel} disabled={isSubmitting} color="default">
                        <CloseOutlined />
                    </IconButton>
                </Stack>
                {/* Duplicate Confirmation Dialog */}
                <Dialog open={showDuplicateConfirm} onClose={handleDuplicateCancel}>
                    <DialogTitle>Duplicate Message</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            A new draft message will be created based on this message's content. Do you want to
                            continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDuplicateCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDuplicateConfirm} color="primary" variant="contained" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    // Draft or new message: Show Exit (inline), Create/Save, and Action Group
    const isCreateMode = !message?.id;
    const hasActionMenu = !isCreateMode && (onUpdateWithoutExit || onSendTest || onPublish || onDuplicate || onDelete);

    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" color="error" disabled={isSubmitting} onClick={handleExitClick}>
                Exit
            </Button>

            {isCreateMode ? (
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting || !isBasicValid}
                    onClick={onCreate}
                >
                    Create
                </Button>
            ) : (
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSubmitting || !isBasicValid}
                    onClick={onUpdate}
                >
                    Save
                </Button>
            )}

            {hasActionMenu && (
                <>
                    <Button
                        variant="outlined"
                        color="inherit"
                        endIcon={<MoreOutlined />}
                        onClick={handleOpenMenu}
                        disabled={isSubmitting}
                    >
                        More
                    </Button>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleCloseMenu}
                        anchorOrigin={{
                            vertical: placement === 'top' ? 'bottom' : 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: placement === 'top' ? 'top' : 'bottom',
                            horizontal: 'right',
                        }}
                        slotProps={{
                            paper: {
                                elevation: 8,
                                sx: {
                                    mt: placement === 'top' ? 1 : -1,
                                    mb: placement === 'bottom' ? 1 : 0,
                                    border: '1px solid',
                                    borderColor: 'grey.400',
                                    minWidth: 200,
                                },
                            },
                        }}
                    >
                        {onUpdateWithoutExit && (
                            <MenuItem
                                onClick={() => handleMenuAction(onUpdateWithoutExit)}
                                disabled={isSubmitting || !isBasicValid}
                            >
                                <ListItemIcon>
                                    <SaveOutlined style={{ fontSize: '1.2rem', color: '#1976d2' }} />
                                </ListItemIcon>
                                <ListItemText>Save Without Exit</ListItemText>
                            </MenuItem>
                        )}

                        {onDuplicate && (
                            <MenuItem onClick={handleDuplicateClick} disabled={isSubmitting}>
                                <ListItemIcon>
                                    <CopyOutlined style={{ fontSize: '1.2rem', color: '#1976d2' }} />
                                </ListItemIcon>
                                <ListItemText>Duplicate</ListItemText>
                            </MenuItem>
                        )}

                        {(onUpdateWithoutExit || onDuplicate) && onPublish && <Divider />}

                        {onSendTest && (
                            <MenuItem onClick={handleSendTestClick} disabled={isSubmitting || !isValid}>
                                <ListItemIcon>
                                    <SendOutlined style={{ fontSize: '1.2rem', color: '#ed6c02' }} />
                                </ListItemIcon>
                                <ListItemText>Send Test</ListItemText>
                            </MenuItem>
                        )}

                        {onPublish && (
                            <MenuItem onClick={handlePublishClick} disabled={isSubmitting || !isValid}>
                                <ListItemIcon>
                                    <RocketOutlined style={{ fontSize: '1.2rem', color: '#ed6c02' }} />
                                </ListItemIcon>
                                <ListItemText>Publish</ListItemText>
                            </MenuItem>
                        )}

                        {(onUpdateWithoutExit || onSendTest || onDuplicate || onPublish) && onDelete && <Divider />}

                        {onDelete && (
                            <MenuItem onClick={handleDeleteClick} disabled={isSubmitting}>
                                <ListItemIcon>
                                    <DeleteOutlined style={{ fontSize: '1.2rem', color: '#d32f2f' }} />
                                </ListItemIcon>
                                <ListItemText>Delete</ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}

            {/* Exit Confirmation Dialog */}
            <Dialog open={showExitConfirm} onClose={handleExitCancel}>
                <DialogTitle>Confirm Exit</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to exit? Any unsaved changes will be discarded.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleExitCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleExitConfirm} color="error" variant="contained" autoFocus>
                        Exit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteConfirm} onClose={handleDeleteCancel}>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <strong>This action cannot be undone.</strong> The message will be moved to the archived
                        section.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Confirmation Dialog */}
            <Dialog open={showDuplicateConfirm} onClose={handleDuplicateCancel}>
                <DialogTitle>Duplicate Message</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        A new draft message will be created based on this message's content. Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDuplicateCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleDuplicateConfirm} color="primary" variant="contained" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Test Confirmation Dialog */}
            <Dialog open={showSendTestConfirm} onClose={handleSendTestCancel}>
                <DialogTitle>Send Test Message</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The current message information will be saved and a notification will be sent to{' '}
                        <strong>your devices only</strong>. This is for previewing purposes on your registered devices.
                        <br />
                        <br />
                        Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSendTestCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSendTestConfirm} color="warning" variant="contained" autoFocus>
                        Send Test
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Publish Confirmation Dialog */}
            <Dialog open={showPublishConfirm} onClose={handlePublishCancel}>
                <DialogTitle>Publish Message</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <Typography variant="body1" fontWeight={700} paragraph>
                            Please double-check all message content before publishing:
                        </Typography>
                        <ul style={{ margin: '0 0 16px 0', paddingLeft: '24px' }}>
                            <li>
                                <Typography variant="body2" paragraph>
                                    The message will be saved and marked as ready for delivery
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" paragraph>
                                    Every 5 minutes, the system checks and sends all pending messages
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2" paragraph>
                                    Successfully published message will be included in the next delivery cycle
                                </Typography>
                            </li>
                        </ul>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePublishCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handlePublishConfirm} color="warning" variant="contained" autoFocus>
                        Publish
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default MessageFormActionButton;
